export const METRIC_COMPONENT_TYPES = [
  { value: "BIG_NUMBER", label: "Big Number" },
  { value: "SMALL_NUMBER", label: "Small Number" },
  { value: "PROGRESS_BAR", label: "Progress Bar" },
  { value: "STATUS", label: "Status" },
];

export const METRIC_SOURCE = {
  INFORM_METRIC: "INFORM_METRIC",
  EARNINGS: "EARNINGS",
};

/**
 * Earnings is always offered alongside the Inform fields on number columns.
 * `isEngageData` marks it as Engage-sourced: it resolves to EARNINGS and
 * carries no Inform node binding.
 */
export const PARTICIPANT_EARNINGS = {
  text: "Participant Earnings",
  value: "PARTICIPANT_EARNINGS",
  isEngageData: true,
};

/** Map Inform `{ name, type }` fields into v-select items. */
export function toMetricSelectItems(fields) {
  return (fields ?? []).map((field) => ({
    text: field.name,
    value: field.name,
    type: field.type,
  }));
}

export function withParticipantEarnings(items) {
  return [...items, PARTICIPANT_EARNINGS];
}

export const METRIC_OPTIONS_REASON = {
  NO_METRIC_NODE: "NO_METRIC_NODE",
  NO_RULE_SET: "NO_RULE_SET",
};

const METRIC_OPTIONS_EMPTY_MESSAGES = {
  [METRIC_OPTIONS_REASON.NO_METRIC_NODE]:
    "This program doesn't have a Metric node yet. Add one in the ruleset to choose metric fields.",
  [METRIC_OPTIONS_REASON.NO_RULE_SET]:
    "This program doesn't have a ruleset yet. Save a ruleset with a Metric node to choose metric fields.",
};

export function metricOptionsEmptyMessage(reason) {
  return (
    METRIC_OPTIONS_EMPTY_MESSAGES[reason] ?? "No metric fields are available for this program."
  );
}

/**
 * A blank ProgramMetricColumn. Every attribute the model defines is present so
 * the object round-trips against the API without a translation step; the ones
 * that don't apply to `componentType` stay null.
 *
 * `columnId`, `rowId` and `slotIndex` are owned by the row, not this form.
 */
export function emptyMetricColumn(componentType = null) {
  return {
    componentType,
    metricSource: null,
    informMetricId: null,
    informMetricAttribute: null,
    prefixLabelAttribute: null,
    suffixLabelAttribute: null,
    labelAttribute: null,
    rangeStartLabelAttribute: null,
    rangeEndLabelAttribute: null,
    statusMetLabelAttribute: null,
    statusNotMetLabelAttribute: null,
    visibilityMetricAttribute: null,
  };
}

/** Select value for a column's source binding. */
export function toMetricSelectValue(column, options) {
  if (column.metricSource === METRIC_SOURCE.EARNINGS) {
    const engageOption = (options ?? []).find((item) => item.isEngageData);
    return engageOption ? engageOption.value : null;
  }
  return column.informMetricAttribute ?? null;
}

/** Source binding for a select value. Inform rows stamp the program's UUID. */
export function toMetricBinding(selectValue, options, informProgramId) {
  const option = (options ?? []).find((item) => item.value === selectValue);

  if (!option) {
    return { metricSource: null, informMetricId: null, informMetricAttribute: null };
  }

  if (option.isEngageData) {
    return {
      metricSource: METRIC_SOURCE.EARNINGS,
      informMetricId: null,
      informMetricAttribute: null,
    };
  }

  return {
    metricSource: METRIC_SOURCE.INFORM_METRIC,
    informMetricId: informProgramId ?? null,
    informMetricAttribute: option.value,
  };
}

/** Display label for a component type, e.g. BIG_NUMBER -> "Big Number". */
export function metricComponentLabel(componentType) {
  const type = METRIC_COMPONENT_TYPES.find((item) => item.value === componentType);
  return type ? type.label : "";
}

/** Label attributes store the Inform field name, which is also the display text. */
export function labelAttributeText(attribute) {
  return attribute || "";
}

/** Visibility attributes store the Inform field name, which is also the display text. */
export function visibilityAttributeLabel(attribute) {
  return attribute || "";
}

/** Mirrors the API rule that INFORM_METRIC needs both node fields. */
export function hasMetricBinding(column) {
  if (column.metricSource === METRIC_SOURCE.EARNINGS) return true;
  return (
    column.metricSource === METRIC_SOURCE.INFORM_METRIC &&
    !!column.informMetricId &&
    !!column.informMetricAttribute
  );
}
